import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export interface Crumb {
  name: string;
  path: string;
}

interface PageShellProps {
  title: string;
  description: string;
  canonicalPath: string;
  crumbs?: Crumb[];
  jsonLd?: object[];
  children: React.ReactNode;
}

const BASE = 'https://precisionvapour.co.nz';

const PageShell: React.FC<PageShellProps> = ({
  title,
  description,
  canonicalPath,
  crumbs = [],
  jsonLd = [],
  children,
}) => {
  const url = `${BASE}${canonicalPath}`;

  const breadcrumbSchema = crumbs.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE}/` },
          ...crumbs.map((c, i) => ({
            '@type': 'ListItem',
            position: i + 2,
            name: c.name,
            item: `${BASE}${c.path}`,
          })),
        ],
      }
    : null;

  return (
    <div className="flex flex-col min-h-screen overflow-hidden">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        {breadcrumbSchema && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        )}
        {jsonLd.map((obj, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(obj)}
          </script>
        ))}
      </Helmet>
      <Navbar />
      <main className="flex-1 pt-24">
        {crumbs.length > 0 && (
          <nav
            aria-label="Breadcrumb"
            className="container-wide mx-auto px-4 md:px-8 pt-4 text-xs text-muted-foreground"
          >
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="hover:text-primary transition-colors">
                  Home
                </Link>
              </li>
              {crumbs.map((c, i) => (
                <li key={c.path} className="flex items-center gap-1.5">
                  <ChevronRight className="w-3 h-3" />
                  {i === crumbs.length - 1 ? (
                    <span className="text-foreground">{c.name}</span>
                  ) : (
                    <Link to={c.path} className="hover:text-primary transition-colors">
                      {c.name}
                    </Link>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default PageShell;
